import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LicenseCheckAddInData } from "../models/LicenseCheckData";
import { LicenseCheckDisplayData } from "../models/LicenseCheckDisplayData";

import { NextCheckAddInData } from "../models/NextCheckData";
import licenseCheckRowsByUser from "../utils/licenseCheckRowsByUser";
import AddInIds from "@/constants/AddInIds";
import { useRawAddinData } from "@/hooks/addins/useAddinData";
import { useUsersWithKeys } from "@/hooks/users/useUsersWithKeys";

interface Args {
  results: string[];
  userTypes: string[];
  detailedView: boolean;
}

export default function useLatestLicenseChecks({ results, userTypes, detailedView }: Args) {
  const users = useUsersWithKeys();

  const {
    data: licenseCheckData = [],
    refetch,
    isLoading: isLoadingChecks,
  } = useRawAddinData<LicenseCheckAddInData>(AddInIds.licenseCheckData);
  const { data: nextCheckData = [], isLoading: isLoadingDates } =
    useRawAddinData<NextCheckAddInData>(AddInIds.nextLicenseCheckDate);
  // licenseCheckRowsByUser translates via i18next; `t` changes with the language.
  const { t } = useTranslation();

  // useMemo, not useState + useEffect: the AddInData placeholder is a new [] on every render, which
  // re-ran the effect and its setState until the fetch resolved.
  const licenseCheckDisplayData = useMemo<LicenseCheckDisplayData[]>(() => {
    const licenseChecks = licenseCheckData.map(check => ({ id: check.id, ...check.details }));
    const nextCheckDates = nextCheckData.map(check => ({ id: check.id, ...check.details }));
    const rows =
      users.data?.drivers.map((user, id) => {
        const checks = licenseCheckRowsByUser(user, id, licenseChecks, nextCheckDates);
        return detailedView
          ? checks
          : checks.reduce((prev, current) =>
              new Date(current.lastVerification).getTime() >
              new Date(prev.lastVerification).getTime()
                ? current
                : prev,
            );
      }) ?? [];
    return rows.flat();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t is a dependency only to recompute on a language change
  }, [detailedView, licenseCheckData, nextCheckData, t, users.data]);

  return {
    data: licenseCheckDisplayData.filter(
      data =>
        (results.length === 0 || results.includes(data.statusMessage)) &&
        (userTypes.length === 0 || userTypes.includes(data.userType)),
    ),
    refetch: () => {
      void users.refetch();
      return refetch();
    },
    isLoading: isLoadingChecks || isLoadingDates || users.isLoading,
    isUsersError: users.isError,
    usersError: users.error,
    isUsersTruncated: users.data?.isTruncated ?? false,
  };
}
